/** @module @liquidefy/code-editor/circuits/onboarding-experience @version 1.0.0 @license LQD Research LLC */
/** @rust onboarding_experience.rs — false; 4/2/26; sm */

/**
 * Tier 5 — Elite: Full Beginner Experience
 *
 * Combines onboarding, tutorials, achievements, quick actions,
 * natural language command translation, output digestion,
 * theming, and workspace into a complete first-run experience.
 *
 * Guides users from "what is a terminal?" to productive contributor
 * through progressive disclosure, gamification, and adaptive teaching.
 *
 * Pure circuit. No DOM, no framework coupling.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import { buildHealthReport, emptyStats, recordSuccess, recordError, healthFromStats, formatStatsDetails } from './circuit-types.js';

import type {
  WorkspaceState,
  ThemeDefinition,
  EditorResult,
} from '../code-editor-types.js';

import { ok, err, generateId, now } from '../code-editor.js';
import type { SkillLevel, SkillAssessment, Tutorial, TutorialStep, Achievement, OnboardingState } from '../terminal/welcome/onboarding.js';
import { createOnboardingState, assessSkillLevel, getSkillLevel } from '../terminal/welcome/onboarding.js';
import type { QuickAction } from '../terminal/welcome/actions.js';
import { createActionState, getAvailableActions } from '../terminal/welcome/actions.js';
import type { OutputDigest } from '../terminal/welcome/digest.js';
import { createDigestState, digestOutput } from '../terminal/welcome/digest.js';
import type { NLTranslation } from '../terminal/guided/palette.js';
import { createPaletteState, translateNaturalLanguage } from '../terminal/guided/palette.js';
import { createLiquidefyDark, createLiquidefyLight } from '../themes/identity/presets.js';
import { createDefaultSurfaces } from '../themes/glass/surfaces.js';
import { createWorkspace } from '../workspace/workspace.js';
import { createFocusState, enterFocusMode as doFocusEnter } from '../workspace/navigation/focus.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 TYPES
// ═══════════════════════════════════════════════════════════════════════════════

export interface OnboardingExperienceConfig {
  readonly userId?: string;
  readonly colorScheme?: 'dark' | 'light';
  readonly showAchievements?: boolean;
  readonly showTutorials?: boolean;
}

export interface OnboardingExperienceHandle extends CircuitHandle {
  getOnboardingState(): OnboardingState;
  getSkillLevel(): SkillLevel;
  startTutorial(tutorialId: string): boolean;
  advanceTutorial(command: string): { matched: boolean; explanation: string; isComplete: boolean };
  exitTutorial(): void;
  getAvailableTutorials(): Tutorial[];
  suggestNextTutorial(): Tutorial | null;
  checkAchievement(command: string): Achievement | null;
  getAchievements(): Achievement[];
  getQuickActions(): QuickAction[];
  getWelcomeMessage(): string;
  translateNL(input: string): { command: string; confidence: number };
  digestOutput(command: string, output: string, exitCode: number): OutputDigest;
  getTheme(): ThemeDefinition;
  getWorkspace(): WorkspaceState;
  recordCommand(command: string): void;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 TUTORIAL LIBRARY
// ═══════════════════════════════════════════════════════════════════════════════

const TUTORIALS: Tutorial[] = [
  {
    id: 'first-steps', title: 'First Steps in the Terminal', description: 'Learn the very basics: where you are and what is around you',
    category: 'basics', requiredLevel: 'beginner',
    steps: [
      { instruction: 'Type `pwd` to see your current directory', expectedCommand: 'pwd', hint: 'pwd stands for "print working directory"', explanation: 'This shows the full path of where you are in the file system', allowAlternatives: [] },
      { instruction: 'Type `ls` to list files in the current directory', expectedCommand: 'ls', hint: 'ls is short for "list"', explanation: 'This displays all visible files and folders', allowAlternatives: ['ls -a', 'ls -la', 'ls -l'] },
      { instruction: 'Type `echo "Hello World"` to print a message', expectedCommand: 'echo "Hello World"', hint: 'echo repeats whatever you give it', explanation: 'echo outputs text to the terminal — the simplest possible program', allowAlternatives: ["echo 'Hello World'", 'echo Hello World'] },
    ],
    estimatedMinutes: 3,
  },
  {
    id: 'navigate-dirs', title: 'Navigating Directories', description: 'Move around the file system with confidence',
    category: 'navigation', requiredLevel: 'beginner',
    steps: [
      { instruction: 'Type `cd ..` to go up one directory', expectedCommand: 'cd ..', hint: '.. means "parent directory"', explanation: 'cd changes your current directory — .. is the parent', allowAlternatives: [] },
      { instruction: 'Type `ls -la` to see all files with details', expectedCommand: 'ls -la', hint: '-l means long format, -a means show hidden files', explanation: 'Hidden files start with a dot. -la shows everything.', allowAlternatives: ['ls -al'] },
      { instruction: 'Type `cd ~` to go to your home directory', expectedCommand: 'cd ~', hint: '~ is a shortcut for your home folder', explanation: 'Your home directory is where your personal files live', allowAlternatives: ['cd', 'cd $HOME'] },
    ],
    estimatedMinutes: 5,
  },
  {
    id: 'file-operations', title: 'Creating and Managing Files', description: 'Create, read, copy, and move files',
    category: 'files', requiredLevel: 'beginner',
    steps: [
      { instruction: 'Type `touch hello.txt` to create a new empty file', expectedCommand: 'touch hello.txt', hint: 'touch creates a file if it does not exist', explanation: 'touch creates an empty file — you can then edit it with any text editor', allowAlternatives: [] },
      { instruction: 'Type `cat hello.txt` to view its contents', expectedCommand: 'cat hello.txt', hint: 'cat displays file contents (it will be empty)', explanation: 'cat concatenates and displays file contents. An empty file shows nothing.', allowAlternatives: [] },
      { instruction: 'Type `cp hello.txt hello-copy.txt` to copy the file', expectedCommand: 'cp hello.txt hello-copy.txt', hint: 'cp means copy — first arg is source, second is destination', explanation: 'cp duplicates a file. The original stays untouched.', allowAlternatives: [] },
      { instruction: 'Type `rm hello-copy.txt` to remove the copy', expectedCommand: 'rm hello-copy.txt', hint: 'rm means remove — be careful, it is permanent!', explanation: 'rm permanently deletes a file. There is no trash can in the terminal.', allowAlternatives: [] },
    ],
    estimatedMinutes: 7,
  },
  {
    id: 'git-intro', title: 'Introduction to Git', description: 'Track your code changes with version control',
    category: 'git', requiredLevel: 'intermediate',
    steps: [
      { instruction: 'Type `git status` to see the state of your repository', expectedCommand: 'git status', hint: 'git status shows what has changed', explanation: 'Status shows modified, staged, and untracked files in your repository', allowAlternatives: [] },
      { instruction: 'Type `git log --oneline -5` to see recent commits', expectedCommand: 'git log --oneline -5', hint: '--oneline compresses each commit to one line', explanation: 'The log shows the history of changes, most recent first', allowAlternatives: ['git log --oneline'] },
      { instruction: 'Type `git branch` to see all branches', expectedCommand: 'git branch', hint: 'Branches let you work on features without affecting main', explanation: 'The asterisk marks your current branch', allowAlternatives: ['git branch -a'] },
    ],
    estimatedMinutes: 10,
  },
  {
    id: 'pipes-and-grep', title: 'Pipes and Searching', description: 'Chain commands together and search for text',
    category: 'advanced', requiredLevel: 'intermediate',
    steps: [
      { instruction: 'Type `ls | wc -l` to count files in current directory', expectedCommand: 'ls | wc -l', hint: '| (pipe) sends output of one command to another', explanation: 'The pipe operator connects commands — ls lists files, wc -l counts lines', allowAlternatives: [] },
      { instruction: 'Type `grep -r "TODO" .` to find all TODOs in your code', expectedCommand: 'grep -r "TODO" .', hint: 'grep searches for patterns, -r means recursive', explanation: 'grep is the most powerful text search tool in the terminal', allowAlternatives: ['grep -ri "TODO" .', 'grep -rn "TODO" .'] },
    ],
    estimatedMinutes: 8,
  },
  {
    id: 'process-management', title: 'Process Management', description: 'Run, monitor, and control processes',
    category: 'system', requiredLevel: 'advanced',
    steps: [
      { instruction: 'Type `ps aux | head -20` to see running processes', expectedCommand: 'ps aux | head -20', hint: 'ps lists processes, head limits output', explanation: 'Every running program is a process with a unique PID', allowAlternatives: ['ps aux'] },
      { instruction: 'Type `top -l 1 | head -15` to see system resource usage', expectedCommand: 'top -l 1 | head -15', hint: 'top shows CPU and memory usage in real time', explanation: 'Monitoring resources helps you find bottlenecks', allowAlternatives: ['top'] },
    ],
    estimatedMinutes: 10,
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// §4 ACHIEVEMENT SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

interface AchievementRule {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly icon: string;
  readonly category: string;
  readonly check: (commands: string[], uniqueCommands: Set<string>) => boolean;
}

const ACHIEVEMENT_RULES: AchievementRule[] = [
  { id: 'first-command', title: 'Hello, Terminal!', description: 'Run your first command', icon: 'star', category: 'milestone', check: (cmds) => cmds.length >= 1 },
  { id: 'ten-commands', title: 'Getting Warmed Up', description: 'Run 10 commands', icon: 'fire', category: 'milestone', check: (cmds) => cmds.length >= 10 },
  { id: 'fifty-commands', title: 'In the Flow', description: 'Run 50 commands', icon: 'bolt', category: 'milestone', check: (cmds) => cmds.length >= 50 },
  { id: 'hundred-commands', title: 'Terminal Veteran', description: 'Run 100 commands', icon: 'medal', category: 'milestone', check: (cmds) => cmds.length >= 100 },
  { id: 'five-unique', title: 'Expanding Vocabulary', description: 'Use 5 different commands', icon: 'book', category: 'exploration', check: (_, uniq) => uniq.size >= 5 },
  { id: 'fifteen-unique', title: 'Polyglot', description: 'Use 15 different commands', icon: 'globe', category: 'exploration', check: (_, uniq) => uniq.size >= 15 },
  { id: 'thirty-unique', title: 'Command Master', description: 'Use 30 different commands', icon: 'crown', category: 'exploration', check: (_, uniq) => uniq.size >= 30 },
  { id: 'first-git', title: 'Version Controlled', description: 'Use a git command', icon: 'branch', category: 'tools', check: (cmds) => cmds.some(c => c.startsWith('git')) },
  { id: 'first-pipe', title: 'Pipe Dream', description: 'Use a pipe (|) in a command', icon: 'link', category: 'tools', check: (cmds) => cmds.some(c => c.includes('|')) },
  { id: 'first-redirect', title: 'Redirect Master', description: 'Use output redirection (> or >>)', icon: 'arrow', category: 'tools', check: (cmds) => cmds.some(c => c.includes('>')) },
  { id: 'first-grep', title: 'Searcher', description: 'Search for text with grep', icon: 'search', category: 'tools', check: (cmds) => cmds.some(c => c.startsWith('grep') || c.includes('| grep')) },
  { id: 'first-ssh', title: 'Remote Explorer', description: 'Connect to a remote machine', icon: 'cloud', category: 'advanced', check: (cmds) => cmds.some(c => c.startsWith('ssh')) },
];

// ═══════════════════════════════════════════════════════════════════════════════
// §5 WELCOME MESSAGE GENERATOR
// ═══════════════════════════════════════════════════════════════════════════════

function generateWelcomeMessage(skillLevel: SkillLevel, userId?: string): string {
  const greeting = userId ? `Welcome back, ${userId}!` : 'Welcome to Liquidefy Terminal!';

  switch (skillLevel) {
    case 'beginner':
      return `${greeting}\n\nThis is your command center. Try typing a command below.\nNot sure where to start? Type a question in plain English and we will translate it into a command.\n\nSuggested first steps:\n  - Type "pwd" to see where you are\n  - Type "ls" to see what files are here\n  - Start a tutorial from the menu to learn step by step`;
    case 'intermediate':
      return `${greeting}\n\nYour terminal is ready. Quick actions are available on the right.\nTry: git status, npm test, or explore with "find . -name '*.ts'"`;
    case 'advanced':
      return `${greeting}\n\nAll systems go. ${TUTORIALS.filter(t => t.requiredLevel === 'advanced').length} advanced tutorials available.\nQuick: ctrl+r for reverse search, !! to repeat last command`;
    case 'expert':
      return `${greeting}\n\nTerminal ready.`;
    default:
      return greeting;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 FACTORY — createOnboardingExperience
// ═══════════════════════════════════════════════════════════════════════════════

export function createOnboardingExperience(config: OnboardingExperienceConfig): OnboardingExperienceHandle {
  let circuitStats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ── State ──
  const userId = config.userId;
  const colorScheme = config.colorScheme ?? 'dark';
  const showAchievements = config.showAchievements ?? true;
  const showTutorials = config.showTutorials ?? true;

  const commandHistory: string[] = [];
  const uniqueCommands = new Set<string>();
  let skillLevel: SkillLevel = 'beginner';
  const unlockedAchievements: Achievement[] = [];
  const unlockedIds = new Set<string>();
  const completedTutorialIds: string[] = [];
  let activeTutorial: { tutorial: Tutorial; step: number } | null = null;

  // ── Workspace ──
  const wsResult = createWorkspace({ name: 'onboarding-workspace', theme: colorScheme });
  let workspaceState: WorkspaceState = wsResult.success ? wsResult.value : ({} as WorkspaceState);

  // ── Theme ──
  const theme = colorScheme === 'light' ? createLiquidefyLight() : createLiquidefyDark();

  // ── Methods ──

  function getOnboardingState(): OnboardingState {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return {
      isFirstRun: commandHistory.length === 0,
      skillAssessment: {
        level: skillLevel,
        commandsUsed: commandHistory.length,
        uniqueCommandsUsed: uniqueCommands.size,
        conceptsLearned: [],
        lastAssessedAt: Date.now(),
      },
      completedTutorials: [...completedTutorialIds],
      achievements: [...unlockedAchievements],
      currentTutorial: activeTutorial
        ? { tutorialId: activeTutorial.tutorial.id, currentStep: activeTutorial.step }
        : undefined,
      preferences: {
        showWelcomeMessage: true,
        showContextualTips: true,
        showAchievements,
        autoSuggestTutorials: showTutorials,
      },
    };
  }

  function getSkillLevel(): SkillLevel {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return skillLevel;
  }

  function startTutorial(tutorialId: string): boolean {
    guardDestroyed();
    const tutorial = TUTORIALS.find(t => t.id === tutorialId);
    if (!tutorial) {
      circuitStats = recordError(circuitStats);
      return false;
    }
    activeTutorial = { tutorial, step: 0 };
    circuitStats = recordSuccess(circuitStats);
    return true;
  }

  function advanceTutorial(command: string): { matched: boolean; explanation: string; isComplete: boolean } {
    guardDestroyed();
    if (!activeTutorial) {
      circuitStats = recordError(circuitStats);
      return { matched: false, explanation: 'No active tutorial', isComplete: false };
    }

    const { tutorial, step } = activeTutorial;
    const currentStep = tutorial.steps[step];
    if (!currentStep) {
      circuitStats = recordError(circuitStats);
      return { matched: false, explanation: 'Tutorial step not found', isComplete: true };
    }

    const normalizedCommand = command.trim().toLowerCase();
    const expectedNormalized = currentStep.expectedCommand?.toLowerCase() ?? '';
    const alternatives = (currentStep.allowAlternatives ?? []).map(a => a.toLowerCase());

    const matched = normalizedCommand === expectedNormalized ||
      normalizedCommand.startsWith(expectedNormalized) ||
      alternatives.some(alt => normalizedCommand === alt || normalizedCommand.startsWith(alt));

    if (matched) {
      const nextStep = step + 1;
      const isComplete = nextStep >= tutorial.steps.length;

      if (isComplete) {
        completedTutorialIds.push(tutorial.id);
        activeTutorial = null;
      } else {
        activeTutorial = { tutorial, step: nextStep };
      }

      circuitStats = recordSuccess(circuitStats);
      return { matched: true, explanation: currentStep.explanation, isComplete };
    }

    circuitStats = recordSuccess(circuitStats);
    return { matched: false, explanation: `Expected: ${currentStep.expectedCommand}. Hint: ${currentStep.hint}`, isComplete: false };
  }

  function exitTutorial(): void {
    guardDestroyed();
    activeTutorial = null;
    circuitStats = recordSuccess(circuitStats);
  }

  function getAvailableTutorials(): Tutorial[] {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    const levelOrder: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'expert'];
    const currentIdx = levelOrder.indexOf(skillLevel);
    const eligible = levelOrder.slice(0, currentIdx + 2); // Current + one above
    return TUTORIALS.filter(t => eligible.includes(t.requiredLevel));
  }

  function suggestNextTutorial(): Tutorial | null {
    guardDestroyed();
    const available = TUTORIALS.filter(
      t => !completedTutorialIds.includes(t.id)
    );

    // Prefer at current level
    const atLevel = available.filter(t => t.requiredLevel === skillLevel);
    if (atLevel.length > 0) {
      circuitStats = recordSuccess(circuitStats);
      return atLevel[0]!;
    }

    // Then below
    const levelOrder: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'expert'];
    const currentIdx = levelOrder.indexOf(skillLevel);
    const below = available.filter(t => levelOrder.indexOf(t.requiredLevel) < currentIdx);
    if (below.length > 0) {
      circuitStats = recordSuccess(circuitStats);
      return below[below.length - 1]!;
    }

    // Then above
    const above = available.filter(t => levelOrder.indexOf(t.requiredLevel) > currentIdx);
    circuitStats = recordSuccess(circuitStats);
    return above.length > 0 ? above[0]! : null;
  }

  function checkAchievement(command: string): Achievement | null {
    guardDestroyed();
    // Record the command first
    doRecordCommand(command);

    // Check for newly unlocked achievements
    for (const rule of ACHIEVEMENT_RULES) {
      if (unlockedIds.has(rule.id)) continue;
      if (rule.check(commandHistory, uniqueCommands)) {
        const achievement: Achievement = {
          id: rule.id,
          title: rule.title,
          description: rule.description,
          icon: rule.icon,
          unlockedAt: Date.now(),
          category: rule.category,
        };
        unlockedAchievements.push(achievement);
        unlockedIds.add(rule.id);
        circuitStats = recordSuccess(circuitStats);
        return achievement;
      }
    }

    circuitStats = recordSuccess(circuitStats);
    return null;
  }

  function getAchievements(): Achievement[] {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return [...unlockedAchievements];
  }

  function getQuickActions(): QuickAction[] {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);

    // Beginner-friendly actions
    const actions: QuickAction[] = [
      { id: 'list-files', label: 'See Files', description: 'List all files in the current folder', icon: 'folder', category: 'files', command: 'ls -la', riskLevel: 'safe', requiresConfirmation: false, tags: ['list', 'files'] },
      { id: 'where-am-i', label: 'Where Am I?', description: 'Show your current location', icon: 'map', category: 'navigation', command: 'pwd', riskLevel: 'safe', requiresConfirmation: false, tags: ['location', 'pwd'] },
      { id: 'go-home', label: 'Go Home', description: 'Navigate to your home folder', icon: 'home', category: 'navigation', command: 'cd ~', riskLevel: 'safe', requiresConfirmation: false, tags: ['home', 'navigate'] },
    ];

    if (skillLevel !== 'beginner') {
      actions.push(
        { id: 'git-status', label: 'Git Status', description: 'Check your repository state', icon: 'git', category: 'git', command: 'git status', riskLevel: 'safe', requiresConfirmation: false, tags: ['git'] },
        { id: 'find-todos', label: 'Find TODOs', description: 'Search for TODO comments in code', icon: 'search', category: 'files', command: 'grep -rn "TODO" .', riskLevel: 'safe', requiresConfirmation: false, tags: ['search', 'todo'] },
      );
    }

    if (skillLevel === 'advanced' || skillLevel === 'expert') {
      actions.push(
        { id: 'disk-usage', label: 'Disk Usage', description: 'See folder sizes', icon: 'chart', category: 'monitoring', command: 'du -sh *', riskLevel: 'safe', requiresConfirmation: false, tags: ['disk', 'usage'] },
      );
    }

    return actions;
  }

  function getWelcomeMessage(): string {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return generateWelcomeMessage(skillLevel, userId);
  }

  function doTranslateNL(input: string): { command: string; confidence: number } {
    guardDestroyed();
    try {
      const result = translateNaturalLanguage(input);
      circuitStats = recordSuccess(circuitStats);
      return { command: result.command, confidence: result.confidence };
    } catch {
      circuitStats = recordError(circuitStats);
      return { command: '', confidence: 0 };
    }
  }

  function doDigestOutput(command: string, output: string, exitCode: number): OutputDigest {
    guardDestroyed();
    try {
      const digest = digestOutput(command, output, exitCode);
      circuitStats = recordSuccess(circuitStats);
      return digest;
    } catch {
      circuitStats = recordError(circuitStats);
      return {
        type: 'raw-output',
        headline: exitCode === 0 ? 'Done!' : 'Something went wrong',
        status: exitCode === 0 ? 'success' : 'error',
        details: [],
        rawOutput: output,
        showRaw: true,
      };
    }
  }

  function getThemeValue(): ThemeDefinition {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return theme;
  }

  function getWorkspace(): WorkspaceState {
    guardDestroyed();
    circuitStats = recordSuccess(circuitStats);
    return workspaceState;
  }

  function doRecordCommand(command: string): void {
    commandHistory.push(command);
    if (commandHistory.length > 5000) commandHistory.shift();
    const base = command.split(' ')[0]?.toLowerCase() ?? '';
    if (base) uniqueCommands.add(base);

    // Reassess skill
    const unique = uniqueCommands.size;
    if (unique >= 40) skillLevel = 'expert';
    else if (unique >= 20) skillLevel = 'advanced';
    else if (unique >= 8) skillLevel = 'intermediate';
    else skillLevel = 'beginner';
  }

  function recordCommand(command: string): void {
    guardDestroyed();
    doRecordCommand(command);
    circuitStats = recordSuccess(circuitStats);
  }

  function getCircuitStats(): CircuitStats {
    return { ...circuitStats, uptime: Date.now() - createdAt };
  }

  function getHealth(): CircuitHealthReport {
    const h = destroyed ? 'destroyed' as const : healthFromStats(circuitStats);
    return buildHealthReport('onboarding-experience', h, formatStatsDetails(circuitStats), createdAt);
  }

  function destroy(): void {
    destroyed = true;
  }

  return Object.freeze({
    name: 'onboarding-experience',
    getOnboardingState,
    getSkillLevel,
    startTutorial,
    advanceTutorial,
    exitTutorial,
    getAvailableTutorials,
    suggestNextTutorial,
    checkAchievement,
    getAchievements,
    getQuickActions,
    getWelcomeMessage,
    translateNL: doTranslateNL,
    digestOutput: doDigestOutput,
    getTheme: getThemeValue,
    getWorkspace,
    recordCommand,
    stats: getCircuitStats,
    health: getHealth,
    destroy,
  });
}
